<template>
  <article v-if="title && link" v-editable="blok" class="flex related-content">
    <section>
      <h6>
        {{ title }}
      </h6>
      <p v-if="description">
        {{ description }}
      </p>
      <NuxtLink
        v-if="isStoryLink"
        :aria-label="title"
        :to="{ path: '/' + link }"
        class="button flex flex-auto-fill"
      >
        <span> More information </span>
        <i class="icon">
          <Icon name="fa6-solid:circle-arrow-right" />
        </i>
      </NuxtLink>
      <a
        v-else
        :aria-label="title + ' - opens in new tab'"
        :href="link"
        class="button flex flex-auto-fill"
        rel="external"
        target="_blank"
      >
        <span> More information </span>
        <i class="icon">
          <Icon name="fa6-solid:circle-arrow-right" />
        </i>
      </a>
    </section>
    <MediaCardImage
      v-if="media && media.filename"
      :fit="mediaFit"
      :max-width="220"
      :media="media"
      :quality="50"
      class="media--card"
    />
  </article>
</template>

<script setup lang="ts">
import type { InlineRelatedContentStoryblok } from '~/types/storyblok'

const props = defineProps<{
  blok: InlineRelatedContentStoryblok
}>()
const description = computed(
  () =>
    props.blok?.description ||
    story.value?.content?.preview ||
    story.value?.content?.description ||
    pageBanner.value?.introduction ||
    story.value?.content?.quote ||
    '',
)
const isStoryLink = computed(() => props.blok?.link?.linktype === 'story')
const link = computed(() => props.blok?.link?.cached_url)
const media = computed(() =>
  props.blok?.media?.filename
    ? props.blok?.media
    : isStoryLink.value
      ? pageBanner.value?.image
      : {},
)
const mediaFit = computed(() => props.blok?.fit || 'cover')
const pageBanner = computed(() => story.value?.content?.intro?.[0])
const title = computed(
  () => props.blok?.title || story.value?.name || 'No title found',
)
const story = computed(() => props.blok?.link?.story || {})
</script>

<style lang="less" scoped>
article.related-content {
  .round(@border-radius-sm);

  margin: @gutter-xl 0 @gutter-xxl;
  background: @color-fill-normal;
  border: @1px solid @color-fill-muted;
  padding: @gutter-sm @gutter-sm @gutter-xs;

  @media @tablet {
    padding: @gutter @gutter-sm @gutter-sm @gutter;
  }

  .media {
    flex-basis: @120px;
    margin-left: @gutter-sm;

    @media @tablet {
      margin-left: @gutter;
    }

    &.media--fit {
      background: @color-fill-pale;
    }
  }

  section {
    flex: 1 1 auto;

    @media @tablet {
      padding-right: @gutter-sm;
    }

    h6 {
      .font-base();

      margin: 0;
    }

    p {
      .font-xs();

      margin: @gutter-sm 0;
    }

    .button {
      padding: 0;

      .button-color(
        transparent,
        @state-info,
        transparent,
        @state-info,
        transparent,
        @color-text,
        transparent,
        @state-info
      );

      span {
        .font-bold();
      }

      .icon {
        .font-size(24, 27);
      }
    }
  }
}
</style>
